import _ from "lodash";
import React, { useState, useEffect } from "react";
import { useI18n } from "Hooks";

// components
import AITutorChatWrapper from "AppComponents/AITutorChatWrapper";
import AITutorAnalysis from "AppComponents/AITutorAnalysis";
import AITutorFlaggedMessages from "AppComponents/AITutorFlaggedMessages";
import UIModal from "UIComponents/UIModal";

// design system
import { Modal, IconButton } from "@toddle-edu/ds-web";
import { CloseOutlined, CaretClosedFilled } from "@toddle-edu/ds-icons";

// third party
import moment from "moment";
import { connect } from "react-redux";
import classNames from "classnames";

// modules
import {
  getStudentAssignmentFromCache,
  getStudentAssignmentDetailsFromCache,
} from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import {
  getUserType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";

// utils
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";

// scss
import classes from "./AITutorChatPreviewModal.scss";

const styles = {
  containerStyle: {
    height: "100%",
  },
  bodyStyle: {
    padding: "0px",
    overflow: "hidden",
  },
  rotateBy90: { transform: "rotate(90deg)" },
};

const AITutorChatPreviewModal = props => {
  const {
    onClose,
    source,
    entityId,
    assessmentId,
    aiConversationId,
    conversationCreatedAt,
  } = props;

  const testId = `assessment-aiTutorChatPreviewModal${
    source ? `-${_.lowerCase(source)}` : ""
  }`;

  const [hasSelectedModality, setHasSelectedModality] = useState(false);
  const [showCollapsedAnalysis, setShowCollapsedAnalysis] = useState(false);

  useEffect(() => {
    if (!hasSelectedModality) {
      setShowCollapsedAnalysis(false);
    }
  }, [hasSelectedModality]);

  const handleModalityStateChange = hasModality => {
    setHasSelectedModality(hasModality);
  };

  const onToggleAnalysisContainer = () => {
    setShowCollapsedAnalysis(prevState => !prevState);
  };

  const renderCollapsedAIAnalysis = () => {
    return (
      <div className={classes.collapsedHeaderContainer}>
        <IconButton
          dsVersion="2.0"
          variant="neutral"
          type="plain"
          size="small"
          icon={<CaretClosedFilled style={styles.rotateBy90} />}
          onClick={onToggleAnalysisContainer}
          testId={`${testId}-toggleIcon`}
        />
        <div className={classNames("text-body", classes.labelContainer)}>
          {t("ai:ai_analysis")}
        </div>
      </div>
    );
  };

  const { t } = useI18n(["ai"]);

  const subtitle = conversationCreatedAt
    ? moment(conversationCreatedAt).format("hh:mm A, Do MMMM YYYY")
    : "";

  const isAITutorModalitiesEnabled = getIsAITutorModalitiesEnabled();

  const bodyContainerClass = classNames({
    [classes.bodyContainer]: true,
    [classes.bodyContainerV2]: isAITutorModalitiesEnabled,
    [classes.revampBodyContainer]:
      isAITutorModalitiesEnabled && hasSelectedModality,
  });

  const bodyLeftContainerClass = classNames({
    [classes.bodyLeftContainer]: !isAITutorModalitiesEnabled,
    [classes.aiTutorChatCon]: isAITutorModalitiesEnabled,
    [classes.revampAITutorChatCon]: isAITutorModalitiesEnabled,
  });

  const bodyRightContainerClass = classNames({
    [classes.bodyRightContainer]: !isAITutorModalitiesEnabled,
    [classes.revampBodyRightContainer]: isAITutorModalitiesEnabled,
  });

  const tutorAnalysisContainerClass = classNames({
    [classes.tutorAnalysisContainer]: isAITutorModalitiesEnabled,
  });

  const customHeaderComponent = (
    <div className={classes.modalHeader}>
      <div className={classes.modalLeftContainer}>
        <h6 style={{ display: "inline" }}>
          {t("ai:view_previous_conversation_history")}
        </h6>
        <span className={classNames("text-body-s", classes.subtitle)}>
          {subtitle}
        </span>
      </div>
      <IconButton
        dsVersion={"2.0"}
        icon={<CloseOutlined />}
        onClick={onClose}
        size={"small"}
        type={"plain"}
        variant={"neutral"}
      />
    </div>
  );

  const bodyComponent = (
    <div className={bodyContainerClass}>
      <div className={bodyLeftContainerClass}>
        <AITutorChatWrapper
          leId={assessmentId}
          entityId={entityId}
          conversationEntityType={"STUDENT_ASSIGNMENT"}
          mode={"view"}
          aiConversationId={aiConversationId}
          source={source}
          showTabs={false}
          showHeader={false}
          containerStyle={styles.containerStyle}
          onModalityStateChange={handleModalityStateChange}
        />
      </div>

      <div className={bodyRightContainerClass}>
        <AITutorFlaggedMessages
          aiConversationId={aiConversationId}
          mode={"PREVIEW"}
        />
        {showCollapsedAnalysis ? (
          renderCollapsedAIAnalysis()
        ) : (
          <div className={tutorAnalysisContainerClass}>
            <AITutorAnalysis
              assessmentId={assessmentId}
              aiConversationId={aiConversationId}
              source={!isAITutorModalitiesEnabled ? source : null}
              hasSelectedModality={hasSelectedModality}
              onToggleAnalysisContainer={onToggleAnalysisContainer}
            />
          </div>
        )}
      </div>
    </div>
  );

  return isAITutorModalitiesEnabled ? (
    <UIModal isFullScreen={true} isOpen={true} onResetClose={onClose}>
      {customHeaderComponent}
      {bodyComponent}
    </UIModal>
  ) : (
    <Modal
      dsVersion={"2.0"}
      width={"1100px"}
      height={"720px"}
      onClose={onClose}
      zIndex={1100}
    >
      <Modal.Header
        title={t("ai:view_previous_conversation_history")}
        subtitle={subtitle}
        isClosable={true}
        hasSeparator={true}
      />
      <Modal.Body style={styles.bodyStyle} isScrollable={false}>
        {bodyComponent}
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { entityId } = ownProps;

  const userType = getUserType();
  const academicYearId = getSelectedAcademicYearId(state);

  const userArchivedFilters = { academicYearId };

  const studentEndVariables = {
    id: entityId,
    userFilter: "ALL",
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
  };

  const variables = {
    id: entityId,
    showOnlySubmitted: false,
    userFilter: userType === "staff" ? "ALL" : "SELF",
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
    userArchivedFilters,
  };

  let studentAssignmentDetails;
  if (userType === "staff") {
    studentAssignmentDetails = getStudentAssignmentDetailsFromCache(variables);
  } else {
    studentAssignmentDetails = getStudentAssignmentFromCache(
      studentEndVariables
    );
  }

  const assessmentId = _.get(studentAssignmentDetails, "assignment.content.id");

  return { assessmentId };
};

export default connect(mapStateToProps, null)(AITutorChatPreviewModal);
